/**
 * 前端直传 OSS 工具
 * 文件不经过后端服务器中转，上传成功后只把文件地址返回给调用方，
 * 由调用方把地址保存到后端，减轻后端服务器的网络传输压力。
 *
 * 依赖 public/index.html 全局加载的 aliyun-oss-sdk（全局变量 OSS），
 * STS 临时凭证通过 hok-admin 的 /upload/securityToken 获取（policy 仅允许 oss:PutObject 到指定目录）。
 */
import { getOssConfig } from '@/api/file'
import { getFileType } from '@/utils/index'

// 生成随机文件名（与 main.js 中 Vue.prototype.$getUuiD 逻辑一致）
function getUuiD(randomLength = 16) {
  return (
    Number(
      Math.random().toString().substr(2, randomLength) + Date.now()
    ).toString(36) +
    Math.floor(Math.random() * 10) +
    1
  )
}

/**
 * base64 字符串转 Blob（支持带 data:image/png;base64, 前缀的 dataURL）
 * @param {String} base64
 * @returns {{ blob: Blob, ext: string }} blob 及图片后缀
 */
export function base64ToBlob(base64) {
  const str = base64 || ''
  const match = str.match(/^data:([\w/+.]+)?(;base64)?,(.*)$/)
  const mime = match && match[1] ? match[1] : 'image/png'
  const raw = match ? match[3] : str
  const byteString = atob(raw)
  const uint8Array = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i)
  }
  const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg')
  return { blob: new Blob([uint8Array], { type: mime }), ext }
}

/**
 * base64 图片直传 OSS（替代 /upload/img/base64 后端中转）
 * @param {Object} options
 * @param {String} options.base64 base64 字符串或 dataURL
 * @param {String} options.dir OSS 目录前缀，图片一般为 'hok_admin'
 * @param {String} [options.subDir] 二级目录，默认 'img'
 * @param {Function} [options.onProgress] 进度回调，入参为 0~100 的数字
 * @returns {{ promise: Promise<{url: string, name: string, size: number}>, cancel: Function }}
 */
export function uploadBase64ToOss({ base64, dir, subDir = 'img', onProgress }) {
  const { blob, ext } = base64ToBlob(base64)
  return uploadFileToOss({
    file: blob,
    dir,
    subDir,
    fileName: `${getUuiD(16)}.${ext}`,
    onProgress
  })
}

/**
 * 直传单个文件到 OSS
 * @param {Object} options
 * @param {File|Blob} options.file 要上传的文件
 * @param {String} options.dir OSS 目录前缀，同时是 STS policy 的授权目录，如 'hok_order'、'hok_material'
 * @param {String} [options.subDir] 目录下的二级分类，如 'img'、'file'，可不传
 * @param {String} [options.fileName] 自定义文件名（不含目录），默认 <随机数>.<原文件后缀>
 * @param {Function} [options.onProgress] 进度回调，入参为 0~100 的数字
 * @returns {{ promise: Promise<{url: string, name: string, size: number}>, cancel: Function }}
 *   promise  resolve 后得到可访问的文件地址等信息
 *   cancel   取消上传（取消后 promise 会以 name === 'cancel' 的错误 reject）
 */
export function uploadFileToOss({ file, dir, subDir, fileName, onProgress }) {
  let client = null
  const promise = getOssConfig(dir).then(res => {
    const ossBucketUrl = res.data.ossBucketUrl
    const suffix = file.name
      ? getFileType(file.name).suffix
      : (file.type && file.type.split('/')[1]) || 'png'
    const objectKey =
      `${dir}${subDir ? '/' + subDir : ''}/` +
      (fileName || `${getUuiD(16)}.${suffix}`)
    const config = {
      region: res.data.region || 'oss-accelerate',
      accessKeyId: res.data.accessKeyId,
      accessKeySecret: res.data.accessKeySecret,
      secure: true,
      bucket: res.data.ossBucket,
      stsToken: res.data.securityToken,
      refreshSTSToken: () =>
        getOssConfig(dir).then(r => ({
          accessKeyId: r.data.accessKeyId,
          accessKeySecret: r.data.accessKeySecret,
          stsToken: r.data.securityToken
        })),
      refreshSTSTokenInterval: 300000
    }
    // eslint-disable-next-line no-undef
    client = new OSS(config)
    return client
      .multipartUpload(objectKey, file, {
        progress: p => {
          onProgress && onProgress(Number((p * 100).toFixed(2)))
        },
        parallel: 4,
        partSize: 1024 * 1024
      })
      .then(() => ({
        url: `${ossBucketUrl}${objectKey}`,
        name: file.name || objectKey.split('/').pop(),
        size: file.size
      }))
  })
  return {
    promise,
    cancel() {
      client && client.cancel()
    }
  }
}
